import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useMeta from '../hooks/useMeta';
import toast from 'react-hot-toast';

const PAGE_SIZE = 50;
const VIEW_KEY = 'accountsView';

const statusTranslation = {
  ACTIVE: 'Ativa',
  INACTIVE: 'Inativa',
  DISABLED: 'Desativada',
};

const statusBadge = {
  ACTIVE: 'bg-emerald-500/10 text-emerald-700',
  DISABLED: 'bg-red-500/10 text-red-700',
};

const GROUPS = [
  { id: 'critical', title: 'Crítico: saldo < R$ 500', icon: 'error', tone: 'text-red-700' },
  { id: 'warning', title: 'Atenção: saldo < R$ 1.000', icon: 'warning', tone: 'text-amber-700' },
  { id: 'regular', title: 'Contas regulares / pós-pagas', icon: 'check_circle', tone: 'text-slate-700' },
];
const GROUP_RANK = { critical: 0, warning: 1, regular: 2 };

const SORTS = [
  { id: 'name', label: 'Nome (A–Z)' },
  { id: 'balance-asc', label: 'Saldo (menor)' },
  { id: 'balance-desc', label: 'Saldo (maior)' },
  { id: 'status', label: 'Status' },
];

const getMetaId = (a) => a.account_id || a.metaAccountId || a.id;
const getStatus = (a) => (a.account_status === 1 ? 'ACTIVE' : a.status || 'UNKNOWN');
const getBalance = (a) => (a.balance === undefined || a.balance === null ? null : Number(a.balance));
const getGroup = (a) => {
  const b = Number(a.balance) || 0;
  if (a.isPrepayAccount && b < 500) return 'critical';
  if (a.isPrepayAccount && b < 1000) return 'warning';
  return 'regular';
};

const fmtBalance = (a) => {
  const b = getBalance(a);
  if (b === null) return '-';
  return `R$ ${b.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'table'; } catch { return 'table'; }
};

const Chip = ({ active, onClick, children, count }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
      active ? 'bg-brand text-white border-brand' : 'bg-white text-slate-700 border-slate-200 hover:border-brand/40'
    }`}
  >
    {children}
    {count !== undefined && <span className={`font-mono tabular-nums ${active ? 'text-white/80' : 'text-slate-500'}`}>{count}</span>}
  </button>
);

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadge[status] || 'bg-slate-500/10 text-slate-600'}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
    {statusTranslation[status] || status}
  </span>
);

const AccountActions = ({ metaId }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <Link
      to={`/reports?account_id=${metaId}`}
      className="bg-brand/5 text-brand border border-brand/20 px-3 py-1.5 rounded-lg font-semibold text-xs hover:bg-brand/10 transition-colors flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
    >
      Gerar relatório
      <span className="material-symbols-outlined text-[16px]" aria-hidden="true">insert_chart</span>
    </Link>
    <Link
      to={`/campaigns/${metaId}`}
      className="text-brand font-semibold text-xs hover:underline flex items-center gap-0.5 px-1 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded"
    >
      Ver campanhas
      <span className="material-symbols-outlined text-[14px]" aria-hidden="true">arrow_forward_ios</span>
    </Link>
  </div>
);

const AccountIcon = ({ isActive }) => (
  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isActive ? 'bg-brand/10 text-brand' : 'bg-slate-100 text-slate-400'}`}>
    <span className="material-symbols-outlined text-[20px]" aria-hidden="true">{isActive ? 'storefront' : 'domain_disabled'}</span>
  </div>
);

const AccountCard = ({ account }) => {
  const metaId = getMetaId(account);
  const status = getStatus(account);
  const low = account.isPrepayAccount && Number(account.balance) < 500;
  return (
    <article className="bg-white border border-slate-200 rounded-xl p-4 shadow-[0_2px_2px_rgba(86,94,116,0.04)] flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <AccountIcon isActive={status === 'ACTIVE'} />
        <div className="min-w-0 flex-1">
          <h4 className="font-headline font-bold text-sm text-on-surface leading-snug break-words">{account.name}</h4>
          <p className="font-mono text-[11px] text-slate-600 truncate">act_{metaId}</p>
        </div>
        <StatusBadge status={status} />
      </div>
      <dl className="grid grid-cols-3 gap-2 text-xs">
        <div><dt className="text-[11px] uppercase tracking-widest text-slate-600">Moeda</dt><dd className="font-medium text-on-surface">{account.currency || '-'}</dd></div>
        <div><dt className="text-[11px] uppercase tracking-widest text-slate-600">Tipo</dt><dd className="font-medium text-on-surface">{account.isPrepayAccount ? 'Pré-pago' : 'Pós-pago'}</dd></div>
        <div><dt className="text-[11px] uppercase tracking-widest text-slate-600">Saldo</dt><dd className={`font-mono font-semibold tabular-nums ${low ? 'text-red-600' : 'text-on-surface'}`}>{fmtBalance(account)}</dd></div>
      </dl>
      <AccountActions metaId={metaId} />
    </article>
  );
};

const AccountsTable = ({ accounts }) => (
  <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50">
        <tr className="text-[11px] uppercase tracking-widest text-slate-600">
          <th scope="col" className="px-4 py-2.5 font-semibold">Conta</th>
          <th scope="col" className="px-4 py-2.5 font-semibold">ID Meta</th>
          <th scope="col" className="px-4 py-2.5 font-semibold">Moeda</th>
          <th scope="col" className="px-4 py-2.5 font-semibold">Tipo</th>
          <th scope="col" className="px-4 py-2.5 font-semibold text-right">Saldo</th>
          <th scope="col" className="px-4 py-2.5 font-semibold">Status</th>
          <th scope="col" className="px-4 py-2.5 font-semibold text-right">Ações</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {accounts.map((account) => {
          const metaId = getMetaId(account);
          const status = getStatus(account);
          const low = account.isPrepayAccount && Number(account.balance) < 500;
          return (
            <tr key={metaId} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <AccountIcon isActive={status === 'ACTIVE'} />
                  <span className="font-headline font-bold text-sm text-on-surface">{account.name}</span>
                </div>
              </td>
              <td className="px-4 py-2.5 font-mono text-xs text-slate-600 whitespace-nowrap">act_{metaId}</td>
              <td className="px-4 py-2.5 text-slate-700">{account.currency}</td>
              <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">{account.isPrepayAccount ? 'Pré-pago' : 'Pós-pago'}</td>
              <td className={`px-4 py-2.5 font-mono font-semibold text-right whitespace-nowrap tabular-nums ${low ? 'text-red-600' : 'text-on-surface'}`}>{fmtBalance(account)}</td>
              <td className="px-4 py-2.5"><StatusBadge status={status} /></td>
              <td className="px-4 py-2.5"><div className="flex justify-end"><AccountActions metaId={metaId} /></div></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const Accounts = () => {
  const { accounts, loading, error, fetchAccounts, syncAccounts, connectMeta } = useMeta();
  const [syncing, setSyncing] = useState(false);
  const location = useLocation();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [balanceFilter, setBalanceFilter] = useState('all'); // all | critical | warning
  const [sort, setSort] = useState('name');
  const [grouped, setGrouped] = useState(true);
  const [view, setView] = useState(readView);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    // Handle OAuth redirect result from Meta
    const params = new URLSearchParams(location.search);
    if (params.get('meta_connected') === 'true') {
      toast.success('Conta Meta conectada com sucesso!');
      // Clean URL without reload
      window.history.replaceState({}, '', '/accounts');
    } else if (params.get('error')) {
      toast.error(`Falha na conexão com o Meta: ${decodeURIComponent(params.get('error'))}`);
      window.history.replaceState({}, '', '/accounts');
    }

    fetchAccounts();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncAccounts();
    } finally {
      setSyncing(false);
    }
  };

  const chooseView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* storage indisponivel */ }
  };

  const currencies = useMemo(
    () => [...new Set(accounts.map(a => a.currency).filter(Boolean))].sort(),
    [accounts]
  );

  const counts = useMemo(() => {
    const c = { active: 0, inactive: 0, critical: 0, warning: 0 };
    accounts.forEach(a => {
      if (getStatus(a) === 'ACTIVE') c.active += 1; else c.inactive += 1;
      const g = getGroup(a);
      if (g === 'critical') c.critical += 1;
      if (g === 'warning') c.warning += 1;
    });
    return c;
  }, [accounts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = accounts.filter(a => {
      if (q) {
        const hay = `${a.name || ''} ${getMetaId(a) || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const isActive = getStatus(a) === 'ACTIVE';
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;
      if (currencyFilter !== 'all' && a.currency !== currencyFilter) return false;
      if (typeFilter === 'prepay' && !a.isPrepayAccount) return false;
      if (typeFilter === 'postpay' && a.isPrepayAccount) return false;
      if (balanceFilter !== 'all' && getGroup(a) !== balanceFilter) return false;
      return true;
    });
    const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' });
    const cmp = {
      name: byName,
      status: (a, b) => (getStatus(a) === 'ACTIVE' ? 0 : 1) - (getStatus(b) === 'ACTIVE' ? 0 : 1) || byName(a, b),
      'balance-asc': (a, b) => (getBalance(a) ?? Infinity) - (getBalance(b) ?? Infinity) || byName(a, b),
      'balance-desc': (a, b) => (getBalance(b) ?? -Infinity) - (getBalance(a) ?? -Infinity) || byName(a, b),
    }[sort];
    return list.sort((a, b) => (grouped ? GROUP_RANK[getGroup(a)] - GROUP_RANK[getGroup(b)] : 0) || cmp(a, b));
  }, [accounts, query, statusFilter, currencyFilter, typeFilter, balanceFilter, sort, grouped]);

  // Paginacao incremental: volta ao inicio quando os filtros mudam
  useEffect(() => { setVisible(PAGE_SIZE); }, [query, statusFilter, currencyFilter, typeFilter, balanceFilter, sort, grouped]);

  const shown = filtered.slice(0, visible);
  const sections = grouped
    ? GROUPS.map(g => ({ ...g, items: shown.filter(a => getGroup(a) === g.id) })).filter(g => g.items.length > 0)
    : [{ id: 'all', items: shown }];

  const hasFilters = query || statusFilter !== 'all' || currencyFilter !== 'all' || typeFilter !== 'all' || balanceFilter !== 'all';
  const clearFilters = () => {
    setQuery(''); setStatusFilter('all'); setCurrencyFilter('all'); setTypeFilter('all'); setBalanceFilter('all');
  };

  const renderItems = (items) => (
    <>
      {view === 'table' && (
        <div className="hidden md:block"><AccountsTable accounts={items} /></div>
      )}
      <div className={`grid gap-3 grid-cols-1 ${view === 'table' ? 'md:hidden' : 'sm:grid-cols-2 xl:grid-cols-3'}`}>
        {items.map(a => <AccountCard key={getMetaId(a)} account={a} />)}
      </div>
    </>
  );

  return (
    <div className="animate-fade-in pb-16 max-w-[1440px] mx-auto space-y-6">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <h1 className="font-headline font-bold text-3xl tracking-tight text-on-surface mb-1">Contas de Anúncios</h1>
          <p className="text-sm text-slate-600 font-body">
            Gerencie suas contas de publicidade do Meta
            {accounts.length > 0 && <span className="font-mono text-slate-500"> · {accounts.length} conectadas</span>}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSync}
            disabled={syncing || loading}
            className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-on-surface hover:bg-slate-50 transition-colors disabled:opacity-50 flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <span className={`material-symbols-outlined text-[18px] ${syncing ? 'animate-spin' : ''}`} aria-hidden="true">sync</span>
            {syncing ? 'Sincronizando...' : 'Sincronizar contas'}
          </button>
          <button
            onClick={connectMeta}
            disabled={loading}
            className="bg-brand text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-brand-deep transition-colors disabled:opacity-50 flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2"
          >
            <span className="material-symbols-outlined text-[18px]" aria-hidden="true">add</span> Conectar Meta
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="rounded-xl bg-error-container border border-error/20 p-4 flex flex-wrap items-start gap-3">
          <span className="material-symbols-outlined text-error mt-0.5" aria-hidden="true">error</span>
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-bold text-on-error-container">{error}</p>
            <p className="text-xs text-on-error-container/80 mt-1">Certifique-se de conectar sua conta Meta primeiro.</p>
          </div>
          <button type="button" onClick={() => fetchAccounts().catch(() => {})} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold hover:bg-slate-50">
            Tentar novamente
          </button>
        </div>
      )}

      {loading && !syncing && accounts.length === 0 ? (
        <div className="space-y-3" role="status" aria-live="polite">
          <span className="sr-only">Carregando contas…</span>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse h-16 bg-white border border-slate-200 rounded-xl" />
          ))}
        </div>
      ) : accounts.length === 0 ? (
        !error && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6 text-slate-400">
              <span className="material-symbols-outlined text-3xl" aria-hidden="true">hub</span>
            </div>
            <p className="font-headline font-bold text-xl text-on-surface">Nenhuma conta encontrada</p>
            <p className="text-sm mt-2 text-slate-600 max-w-sm mx-auto">Conecte sua conta Meta ou sincronize para descobrir as contas disponíveis.</p>
            <button onClick={connectMeta} className="mt-6 bg-brand text-white rounded-lg px-5 py-2 text-sm font-semibold hover:bg-brand-deep transition-colors">
              Conectar conta Meta
            </button>
          </div>
        )
      ) : (
        <>
          <section aria-label="Filtros" className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-slate-400 pointer-events-none" aria-hidden="true">search</span>
                <label htmlFor="accounts-search" className="sr-only">Buscar conta por nome ou ID</label>
                <input
                  id="accounts-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por nome ou ID…"
                  className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                Ordenar
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="border border-slate-200 rounded-lg bg-white px-2 py-2 text-sm text-on-surface focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
                >
                  {SORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
              <div role="group" aria-label="Visualização" className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-xs font-semibold">
                {[{ id: 'table', label: 'Tabela', icon: 'table_rows' }, { id: 'cards', label: 'Cards', icon: 'grid_view' }].map(v => (
                  <button
                    key={v.id}
                    type="button"
                    aria-pressed={view === v.id}
                    onClick={() => chooseView(v.id)}
                    className={`px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${view === v.id ? 'bg-white text-brand shadow-xs' : 'text-slate-700 hover:text-slate-900'}`}
                  >
                    <span className="material-symbols-outlined text-[16px]" aria-hidden="true">{v.icon}</span>{v.label}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input type="checkbox" checked={grouped} onChange={(e) => setGrouped(e.target.checked)} className="w-4 h-4 rounded border-slate-300 accent-brand" />
                Agrupar por saldo
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <div role="group" aria-label="Filtrar por status" className="flex flex-wrap items-center gap-1.5">
                <Chip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>Todas</Chip>
                <Chip active={statusFilter === 'active'} onClick={() => setStatusFilter('active')} count={counts.active}>Ativas</Chip>
                <Chip active={statusFilter === 'inactive'} onClick={() => setStatusFilter('inactive')} count={counts.inactive}>Inativas</Chip>
              </div>
              <div role="group" aria-label="Filtrar por tipo" className="flex flex-wrap items-center gap-1.5">
                <Chip active={typeFilter === 'prepay'} onClick={() => setTypeFilter(typeFilter === 'prepay' ? 'all' : 'prepay')}>Pré-pago</Chip>
                <Chip active={typeFilter === 'postpay'} onClick={() => setTypeFilter(typeFilter === 'postpay' ? 'all' : 'postpay')}>Pós-pago</Chip>
              </div>
              {currencies.length > 1 && (
                <div role="group" aria-label="Filtrar por moeda" className="flex flex-wrap items-center gap-1.5">
                  {currencies.map(c => (
                    <Chip key={c} active={currencyFilter === c} onClick={() => setCurrencyFilter(currencyFilter === c ? 'all' : c)}>{c}</Chip>
                  ))}
                </div>
              )}
              <div role="group" aria-label="Filtrar por saldo" className="flex flex-wrap items-center gap-1.5">
                <Chip active={balanceFilter === 'critical'} onClick={() => setBalanceFilter(balanceFilter === 'critical' ? 'all' : 'critical')} count={counts.critical}>Saldo crítico</Chip>
                <Chip active={balanceFilter === 'warning'} onClick={() => setBalanceFilter(balanceFilter === 'warning' ? 'all' : 'warning')} count={counts.warning}>Atenção</Chip>
              </div>
              {hasFilters && (
                <button type="button" onClick={clearFilters} className="text-xs font-semibold text-brand hover:underline">Limpar filtros</button>
              )}
            </div>
          </section>

          <p className="text-xs text-slate-600" role="status" aria-live="polite">
            {filtered.length === accounts.length ? `${accounts.length} contas` : `${filtered.length} de ${accounts.length} contas`}
          </p>

          {filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
              <span className="material-symbols-outlined text-3xl text-slate-400" aria-hidden="true">search_off</span>
              <p className="font-headline font-bold text-lg text-on-surface mt-2">Nenhuma conta corresponde aos filtros</p>
              <button type="button" onClick={clearFilters} className="mt-3 text-sm font-semibold text-brand hover:underline">Limpar filtros</button>
            </div>
          ) : (
            <div className="space-y-8">
              {sections.map(section => (
                <section key={section.id} aria-label={section.title || 'Contas'}>
                  {section.title && (
                    <h2 className={`font-headline font-bold text-base mb-3 flex items-center gap-2 ${section.tone}`}>
                      <span className="material-symbols-outlined text-[20px]" aria-hidden="true">{section.icon}</span>
                      {section.title}
                      <span className="text-xs font-medium opacity-70">({section.items.length})</span>
                    </h2>
                  )}
                  {renderItems(section.items)}
                </section>
              ))}
              {visible < filtered.length && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setVisible(v => v + PAGE_SIZE)}
                    className="px-5 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                  >
                    Mostrar mais ({filtered.length - visible} restantes)
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Accounts;
